import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, ArrowLeft, MapPin, Plus, Minus } from 'lucide-react';
import { CtaButtons } from './CtaButtons';
import { businessEntity, websiteEntity, injectJsonLd, setHeadMeta, canonicalUrl, BUSINESS_ID, WEBSITE_ID, BUSINESS_NAME } from '../utils/seo';
export interface FAQItem {
  question: string;
  answer: string;
}
export interface BreadcrumbItem {
  name: string;
  item: string;
}
interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  metaDescription: string;
  schema: object;
  /** Optional FAQ list. When provided, renders an accessible FAQ section and injects FAQPage schema. */
  faqs?: FAQItem[];
  /** Optional breadcrumb trail. When provided, renders a visible breadcrumb nav and injects BreadcrumbList schema. */
  breadcrumbs?: BreadcrumbItem[];
  /** On a city landing page, the service-area block mentions only this city. */
  cityName?: string;
}
export function ServicePageLayout({
  title,
  subtitle,
  children,
  metaDescription,
  schema,
  faqs,
  breadcrumbs,
  cityName
}: ServicePageLayoutProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  // Scroll to top only when the page itself changes — not on every re-render
  // (e.g. opening an FAQ), which previously yanked the reader back to the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);
  // Page props arrive as fresh object literals on every render, so key the
  // metadata effect off their serialised value instead of their identity.
  const metaKey = JSON.stringify({
    title,
    metaDescription,
    schema,
    faqs,
    breadcrumbs
  });
  useEffect(() => {
    const pageTitle = `${title} | ${BUSINESS_NAME}`;
    const url = canonicalUrl(window.location.pathname);
    setHeadMeta({
      title: pageTitle,
      description: metaDescription,
      path: window.location.pathname,
      ogType: 'article'
    });
    // One linked structured-data graph per page: the canonical business and
    // website entities, this page's primary (Service/LocalBusiness) schema, and
    // optional FAQPage + BreadcrumbList — all cross-referenced by @id so search
    // engines resolve them to a single business rather than duplicates.
    const {
      ['@context']: _ignoredContext,
      ...primary
    } = schema as Record<string, unknown>;
    const primaryNode: Record<string, unknown> = {
      '@id': `${url}#primary`,
      isPartOf: {
        '@id': WEBSITE_ID
      },
      ...primary,
      mainEntityOfPage: url
    };
    // Point any inline provider at the single canonical business entity so the
    // site never declares competing LocalBusiness records.
    if (primaryNode.provider) {
      primaryNode.provider = {
        '@id': BUSINESS_ID
      };
    }
    const graph: object[] = [businessEntity, websiteEntity, primaryNode];
    if (faqs && faqs.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        isPartOf: {
          '@id': WEBSITE_ID
        },
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer
          }
        }))
      });
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.item
        }))
      });
    }
    return injectJsonLd(graph);
  }, [metaKey]);
  return <main className="w-full bg-black text-white" data-id="element-442" data-mp="3khwb7awvjzl9">
      {/* Hero Section */}
      <section className="relative pt-36 sm:pt-40 pb-16 px-4 border-b border-zinc-900 overflow-hidden" data-id="element-443" data-mp="c35jmygb2ghh">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/20 blur-[120px] pointer-events-none rounded-full" data-id="element-444" data-mp="gudr3oy2rrs0" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-445" data-mp="2p93srtik15jj">
          {/* Breadcrumb navigation */}
          {breadcrumbs && breadcrumbs.length > 0 ? <nav aria-label="Breadcrumb" className="mb-8" data-id="element-446" data-mp="2fch8p6y1yiik">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400" data-id="element-447" data-mp="2ff3i7c4i783f">
                {breadcrumbs.map((b, i) => <li key={b.item} className="flex items-center gap-2" data-id="element-448" data-mp="381ogkwjeckgh" data-mpi={i}>
                    {i < breadcrumbs.length - 1 ? <Link to={b.item.replace('https://www.fulldetailpro.com', '') || '/'} className="hover:text-white transition-colors" data-id="element-449" data-mp="332lenkypqucq">
                        {b.name}
                      </Link> : <span className="text-gray-300" aria-current="page" data-id="element-450" data-mp="2y9ifmxb62sp5">
                        {b.name}
                      </span>}
                    {i < breadcrumbs.length - 1 && <span className="text-zinc-600" data-id="element-451" data-mp="3qsxpp4p9977j">/</span>}
                  </li>)}
              </ol>
            </nav> : <Link to="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group" data-id="element-452" data-mp="3lzw6wllxgnio">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-453" data-mp="3ucg2uepgtxko" />
              Back to Home
            </Link>}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-454" data-mp="7547i5e0gxlu">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6" data-id="element-455" data-mp="h4d290gpag99">
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-lg" data-id="element-456" data-mp="l43pwvaxkg9k">
                {title}
              </span>
            </h1>
            <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)] mb-6" data-id="element-457" data-mp="utn77wizw22b" />
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl" data-id="element-458" data-mp="zvcmxph5x3wq">
              {subtitle}
            </p>
            <CtaButtons align="start" className="mt-8" data-id="element-459" data-mp="14te23zbgwj2x" />
          </motion.div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-16 px-4" data-id="element-460" data-mp="a65roa72ccuw">
        <div className="max-w-4xl mx-auto" data-id="element-461" data-mp="a78h0doqm78p">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:text-yellow-400 prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-300 prose-strong:text-white" data-id="element-462" data-mp="outki8piy8h5">
            {children}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="py-16 px-4 border-t border-zinc-900" aria-labelledby="faq-heading" data-id="element-463" data-mp="17scy7pmxgf9w">
          <div className="max-w-4xl mx-auto" data-id="element-464" data-mp="12vwa2vfqa0wt">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-8 text-center" data-id="element-465" data-mp="39l13e4j4tcsb">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-466" data-mp="34nsm065t5cs4">
              {faqs.map((faq, index) => {
            // City pages show every answer openly — no toggles.
            if (cityName) {
              return <div key={index} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5" data-id="element-467" data-mp="1p2gtun8ojdke">
                      <h3 className="text-lg font-bold text-white" data-id="element-468" data-mp="1adkl0nystwiq">
                        {faq.question}
                      </h3>
                      <p className="mt-3 text-gray-300 leading-relaxed" data-id="element-469" data-mp="11dffkjd61877">
                        {faq.answer}
                      </p>
                    </div>;
            }
            const isOpen = openFaq === index;
            return <div key={index} className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-id="element-470" data-mp="rfrj9ozwvsmx" data-mpi={index}>
                    <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-zinc-800/40 transition-colors" aria-expanded={isOpen} data-id="element-471" data-mp="wfdxo1rh7exb">
                      <span className="text-lg font-bold text-white" data-id="element-472" data-mp="2qr14kmnn75u2">
                        {faq.question}
                      </span>
                      {isOpen ? <Minus className="w-5 h-5 text-red-500 shrink-0" data-id="element-473" data-mp="19jvzu04il521" /> : <Plus className="w-5 h-5 text-red-500 shrink-0" data-id="element-474" data-mp="19l7749x64uxw" />}
                    </button>
                    {isOpen && <div className="px-5 pb-5 text-gray-300 leading-relaxed" data-id="element-475" data-mp="14rcol9taum0e">
                        {faq.answer}
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* Service Areas SEO Block */}
      <section className="py-12 px-4 bg-zinc-900/30 border-t border-zinc-900" data-id="element-476" data-mp="pvn6vnv5cv1h">
        <div className="max-w-4xl mx-auto text-center" data-id="element-477" data-mp="lu1stz6vmapp">
          <MapPin className="w-8 h-8 text-red-500 mx-auto mb-4" data-id="element-478" data-mp="lul818556l01" />
          {cityName ? <>
              <h2 className="text-2xl font-bold mb-4" data-id="element-479" data-mp="gx2x5c7kdvpe">
                Proudly Serving {cityName}, CA
              </h2>
              <p className="text-gray-400 leading-relaxed" data-id="element-480" data-mp="2g5zy5q22emgb">
                Mike's Pro Detail provides mobile car detailing, mobile auto
                detailing, interior detailing, exterior detailing, and full car
                detailing in {cityName}. We come directly to your {cityName} home
                or office — no drop-off, no waiting room.{' '}
                <Link to="/locations" className="text-red-500 hover:underline" data-id="element-481" data-mp="2sjwql2mt2nck">
                  View all service areas
                </Link>
                .
              </p>
            </> : <>
          <h2 className="text-2xl font-bold mb-4" data-id="element-482" data-mp="1zv7tdeovf263">
            Proudly Serving Ventura County
          </h2>
          <p className="text-gray-400 leading-relaxed" data-id="element-483" data-mp="24tj37pf61cob">
            We provide top-tier mobile auto detailing in{' '}
            <Link to="/service-areas/camarillo" className="text-red-500 hover:underline" data-id="element-484" data-mp="29u6veef4rzk4">
              Camarillo
            </Link>

            ,{' '}
            <Link to="/service-areas/ventura" className="text-red-500 hover:underline" data-id="element-485" data-mp="3l67mw13dq23k">
              Ventura
            </Link>
            ,{' '}
            <Link to="/service-areas/thousand-oaks" className="text-red-500 hover:underline" data-id="element-486" data-mp="3paxg1i6nl2ks">
              Thousand Oaks
            </Link>
            ,{' '}
            <Link to="/service-areas/simi-valley" className="text-red-500 hover:underline" data-id="element-487" data-mp="1qy1tym3aoflm">
              Simi Valley
            </Link>
            ,{' '}
            <Link to="/service-areas/moorpark" className="text-red-500 hover:underline" data-id="element-488" data-mp="20p65tcxobkc8">
              Moorpark
            </Link>
            ,{' '}
            <Link to="/service-areas/newbury-park" className="text-red-500 hover:underline" data-id="element-489" data-mp="132pa2c4l9esl">
              Newbury Park
            </Link>
            , and{' '}
            <Link to="/service-areas/westlake-village" className="text-red-500 hover:underline" data-id="element-490" data-mp="1cz25pfjp22pz">
              Westlake Village
            </Link>
            . Whether you need car detailing in Camarillo or mobile detailing in
            Ventura County, we come directly to your home or office.
          </p>
            </>}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden border-t border-zinc-900" data-id="element-491" data-mp="hqi1elgir3up">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-black pointer-events-none" data-id="element-492" data-mp="hsaxx7xf47oz" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-493" data-mp="80os5r3un0tm">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase mb-6" data-id="element-494" data-mp="7yu78cskv8pj">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-495" data-mp="3u8ell2ra2fmh">
            Book your professional mobile detailing appointment today. We bring
            the shop to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-496" data-mp="3l6yotxtdyc0q">
            <a href="tel:8183819905" className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-base sm:text-xl whitespace-nowrap rounded-sm transition-colors shadow-[0_0_20px_rgba(220,38,38,0.5)] w-full sm:w-auto justify-center" style={{
            fontSize: "24px",
            width: "395px",
            height: "59px"
          }} data-id="element-497" data-mp="3g83vw31pf72w">
              <Phone className="w-6 h-6" data-id="element-498" data-mp="38vxuqaoen1tg" />
              Call or Text 818-381-9905
            </a>
            <Link to="/contact" className="px-8 py-4 bg-white text-black hover:bg-gray-200 font-bold text-xl uppercase tracking-wider rounded-sm transition-colors w-full sm:w-auto text-center" data-id="element-499" data-mp="3doqzuznscc62">
              Get a Quote
            </Link>
          </div>
        </div>
      </section>
    </main>;
}